import { useState } from 'react';
import { Pencil, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LegalFeeItem } from '@/types/legalFees';
import { formatCurrency } from '@/lib/currency.utils';
import { ItemPaymentForm } from './ItemPaymentForm';
import { EditItemForm } from './EditItemForm';

interface ItemRowProps {
  item: LegalFeeItem;
  stageName: string;
  fixedAssetId: string;
  propertyName: string;
}


export function ItemRow({ item, stageName, fixedAssetId, propertyName }: ItemRowProps) {
  const [payOpen, setPayOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const isPaid = item.paid_status === 'paid';
  const hasPaymentLink = isPaid && item.payments.length > 0;

  return (
    <div className="flex items-start justify-between gap-3 py-3 px-1">
      {/* Left: item details */}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{item.title}</span>
          <span className="text-xs text-muted-foreground">{item.currency}</span>
          <span className="text-sm font-semibold">
            {formatCurrency(item.amount, item.currency)}
          </span>
          {isPaid ? (
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200">
              Paid
            </Badge>
          ) : (
            <Badge variant="secondary">Unpaid</Badge>
          )}
        </div>
        {item.description && (
          <p className="text-xs text-muted-foreground">{item.description}</p>
        )}
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-1 shrink-0">
        {hasPaymentLink && (
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/transactions/${item.payments[0].transaction_id}`}>
              <ExternalLink className="h-4 w-4" />
              <span className="sr-only">View transaction</span>
            </Link>
          </Button>
        )}

        {!isPaid && (
          <Button size="sm" variant="outline" onClick={() => setPayOpen(true)}>
            Pay
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setEditOpen(true)}
          aria-label="Edit item"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>

      {/* Pay dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pay: {item.title}</DialogTitle>
          </DialogHeader>
          <ItemPaymentForm
            item={item}
            stageName={stageName}
            fixedAssetId={fixedAssetId}
            propertyName={propertyName}
            onSuccess={() => setPayOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit: {item.title}</DialogTitle>
          </DialogHeader>
          <EditItemForm
            item={item}
            fixedAssetId={fixedAssetId}
            onSuccess={() => setEditOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
